/**
 * @file ExpressionMetadata.js.
 * @author Sérgio Ferraz Souza <todentro@gmail.com>
 * @copyright Cognatis GeoAnalytics & Big Data Ltda :: 2023
 */

import Request from '../components/http/request'
import Service from './Service'

const defaultJsonHeaders = {
  headers: {
    'Content-Type': 'application/json'
  }
}

/**
 * Expression Management Service
 * @class
 */
class ExpressionMetadata extends Service {
  constructor(options, resource) {
    super(options)
    this.resource = resource
    this.request = new Request({tag: 'das'})
    // this.request = new Request({environment: 'local', tag: 'das'})
  }
  async getExpressionByMetadata (seletor1, year, metric, category_1 = null, category_2 = null, category_3 = null, category_4 = null, category_5 = null, category_6 = null, seletor2 = null, seletor3 = null, tableId) {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/expressionmetadata/getidexpressionbymetadata'
      const data = {
        'seletor1': seletor1,
        'category_1': category_1,
        'category_2': category_2,
        'category_3': category_3,
        'category_4': category_4,
        'category_5': category_5,
        'category_6': category_6,
        'seletor2': seletor2,
        'seletor3': seletor3,
        'metric': metric,
        'year': year,
        'familyid': tableId
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(res => resolve(res.status === 200 ? res.data[0] : null))
        .catch(() => reject)
    })
  }
  async getExpressionByMetadataMultiSelect (seletor1, year, metric, familyId) {
    return new Promise((resolve, reject) => {
      let endPoint = '/api/0.1-a/expressionmetadata/getidexpressionbymetadatamultiselect'
      var data = {
        'seletor1': seletor1,
        'metric': metric,
        'year': year,
        'familyid': familyId
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response.data[0]))
        .catch(() => reject)
    })
  }
  async getExpressionByMetadataSingleSelect (seletor1, category1, year, metric, tableId) {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/expressionmetadata/getidexpressionbymetadatasingleselect'
      const data = {
        'seletor1': seletor1,
        'category_1': category1,
        'metric': metric,
        'year': year,
        'familyid': tableId
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response.data[0]))
        .catch(() => reject)
    })
  }
  /**
   * Obten o cache do source para o input de uma expression
   *
   * @async
   * @function getExpressionMetaData
   * @param {Number} expressionId - Expression Id
   * @param {String} name - Number of bins
   * @return {Promise<json>} The json with expression buckets
   */
  async getInputSourceCache(expressionId, name = '') {
    return new Promise((resolve, reject) => {
      if (typeof expressionId === 'undefined') reject(new Error('Esperado o identificador da expressão'))
      if (name === '') reject(new Error('Esperado name tag para o cache da expressão'))
      const endPoint = `/api/0.1-a/expressionmetadata/getInputSourceCache`
      this.request.beforeRequest()
      this.request.post(endPoint, { 'expressionid': expressionId, 'name': name })
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  async getMetadados (expression) {
    return new Promise((resolve, reject) => {
      if (typeof expression === 'undefined') reject(new Error('Esperada a expressão'))
      const endPoint = `/api/0.1-a/expressionmetadata/getmetadados/${expression.familyId}?selector=${expression.alias}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  // TODO getMetric e getYear devem lançar uma exception quando não encontram uma relação
  // A tbExpressionMetadata tem uma relação de 1x1 com a tbExpression, logo deveria haver.
  /**
   * Get metric values for an expression
   * @async
   * @function getMetric
   * @param expression Current expression
   * @returns {Promise<Array>} Metric values
   */
  async getMetric (expression) {
    try {
      return new Promise((resolve, reject) => {
        if (typeof expression === 'undefined') reject(new Error('Esperada a expressão'))
        const selector = expression.alias || expression.label || expression.name
      const tableId = expression.familyId
        const endPoint = `/api/0.1-a/expressionmetadata/${tableId}/metric?selector=${selector}`
        this.request.get(endPoint, defaultJsonHeaders)
          .then(res => resolve(res.status === 200 ? res.data : []))
          .catch(() => reject)
      })
    } catch (e) {
      console.warn ("Impossível encontrar a metrica correspondente de uma expression")
      console.warn(expression)
      console.warn("----------")
    }
  }
  async getTreeviewSeletor2 (seletor, tableId) {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/expressionmetadata/treeviewselector2'
      const data = {
        'seletor': seletor,
        'familyid': tableId
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }
  async getYear (expression) {
      return new Promise((resolve, reject) => {
        if (typeof expression === 'undefined') reject(new Error('Esperada a expressão'))

        if ( expression.familyId === 'undefined') {
          console.warn ("Impossível encontrar o Ano correspondente de uma expression")
          console.warn (expression)
          reject(new Error('Esperado um familyId para expressão'))
        }


        const tableId = expression.familyId
        const selector = expression.alias || expression.label || expression.name
        const endPoint = `/api/0.1-a/expressionmetadata/${tableId}/year?selector=${selector}`
        this.request.get(endPoint, defaultJsonHeaders)
          .then(res => resolve(res.status === 200 ? res.data : []))
          .catch(() => reject)
      })
  }
  /**
   * Search for an expression that contains the provided metadata.
   *
   * @async
   * @function search
   * @param {Array} metadataList - A collection of metadata about expressions to aid in searching.
   * @return {Promise<json>} A collection of target expressions
   */
  async search(metadataList) {
    return new Promise((resolve, reject) => {
      if (typeof metadataList === 'undefined') reject(new Error('Esperada a coleção de metadados.'))
      const endPoint = `/api/0.1-a/expressionmetadata/search`
      this.request.beforeRequest()
      this.request.post(endPoint, metadataList, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
}

export {ExpressionMetadata}
